import { useEffect, useRef } from 'react';
import { ClipboardIcon } from '../../../icons/SvgIcons';
import { EmptyState } from '../../ui/EmptyState/EmptyState';
import Loader from '../../ui/Loader/Loader';
import { EventsTableHeader } from './components/EventsTableHeader/EventsTableHeader';
import { EventsTableRow } from './components/EventsTableRow/EventsTableRow';
import { EventsTableProps } from './EventsTable.types';

export function EventsTable({
  events,
  loading = false,
  hasMore = false,
  onLoadMore,
  sortBy,
  sortOrder,
  onSort,
  onEventUpdated,
  onEventImpactUpdated,
  canEditSeverity = false,
  onRowClick
}: EventsTableProps) {
  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          onLoadMore?.();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, onLoadMore]);
  if (loading && events.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[230px]">
        <Loader />
      </div>
    );
  }

  if (events.length === 0 && !loading) {
    return (
      <EmptyState
        icon={<ClipboardIcon />}
        title="No Logs yet"
        description="Activities will appear here once recorded."
      />
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="w-full rounded-lg bg-white shadow-sm border border-solid border-gray-250v2 relative">
        <table className="w-full table-fixed">
          <EventsTableHeader
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={onSort}
          />
          <tbody>
            {events.map((event) => (
              <EventsTableRow
                key={event.id}
                event={event}
                onEventUpdated={onEventUpdated}
                onEventImpactUpdated={onEventImpactUpdated}
                canEditSeverity={canEditSeverity}
                onRowClick={onRowClick}
              />
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div ref={observerTarget} className="flex justify-center py-4">
          {loading && <Loader />}
        </div>
      )}
    </div>
  );
}
